import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { FormStateModel } from './form.model'
import { FormService } from '../form.service'
import {
  AdminGetInspectionWindows,
  AdminUpdateInspectionWindows,
  GetInspectionCompliance,
  GetOfficialForms,
  GetOneOfficialForm,
  ResetFormHistoryItem,
  SubmitOfficialForm
} from './form.actions'

@State<FormStateModel>({
  name: 'form'
})
@Injectable()
export class FormState {
  constructor(private formService: FormService) {}

  @Selector()
  static formItem(state: FormStateModel) {
    return state.formItem
  }

  @Selector()
  static formHistoryItem(state: FormStateModel) {
    return state.formHistoryItem
  }

  @Selector()
  static formHistory(state: FormStateModel) {
    return (state.formItem?.formHistory ?? []).slice().reverse()
  }

  @Selector()
  static allFormItems(state: FormStateModel) {
    return state.allFormItems
  }

  @Selector()
  static inspectionCompliance(state: FormStateModel) {
    return state.inspectionCompliance
  }

  @Selector()
  static inspectionWindows(state: FormStateModel) {
    return state.inspectionWindows
  }

  @Action(GetInspectionCompliance, { cancelUncompleted: true })
  getInspectionCompliance(ctx: StateContext<FormStateModel>) {
    ctx.patchState({
      inspectionCompliance: undefined
    })
    return this.formService.getInspectionCompliance().pipe(
      tap((res) => {
        ctx.patchState({
          inspectionCompliance: res
        })
      })
    )
  }

  @Action(AdminGetInspectionWindows)
  adminGetInspectionWindows(ctx: StateContext<FormStateModel>) {
    return this.formService.adminGetInspectionWindows().pipe(
      tap((res) => {
        ctx.patchState({
          inspectionWindows: res?.items ?? []
        })
      })
    )
  }

  @Action(AdminUpdateInspectionWindows)
  adminUpdateInspectionWindows(
    ctx: StateContext<FormStateModel>,
    { payload }: AdminUpdateInspectionWindows
  ) {
    return this.formService.adminUpdateInspectionWindows(payload).pipe(
      tap((res) => {
        ctx.patchState({
          inspectionWindows: res?.items ?? []
        })
      })
    )
  }

  @Action(GetOneOfficialForm, { cancelUncompleted: true })
  getOneForm(
    ctx: StateContext<FormStateModel>,
    { payload, isHistory, isWeeklyTireReport }: GetOneOfficialForm
  ) {
    return this.formService.getOneForm(payload, isWeeklyTireReport).pipe(
      tap((res) => {
        if (isHistory) {
          ctx.patchState({
            formHistoryItem: res
          })
        } else {
          ctx.patchState({
            formItem: res,
            formHistoryItem: res
          })
        }
      })
    )
  }

  @Action(GetOfficialForms)
  getForms(ctx: StateContext<FormStateModel>, { payload, isWeeklyTireReport }: GetOfficialForms) {
    ctx.patchState({
      allFormItems: undefined
    })
    return this.formService.getForms(payload, isWeeklyTireReport).pipe(
      tap((res) => {
        ctx.patchState({
          allFormItems: res?.data ?? [],
          formItem: undefined,
          formHistoryItem: undefined
        })
      })
    )
  }

  @Action(SubmitOfficialForm)
  submitForm(ctx: StateContext<FormStateModel>, { payload, isWeeklyTireReport }: SubmitOfficialForm) {
    return this.formService.submitForm(payload, isWeeklyTireReport).pipe(
      tap((res) => {
        ctx.patchState({
          formItem: res,
          formHistoryItem: res
        })
      })
    )
  }

  @Action(ResetFormHistoryItem)
  resetFormHistoryItem(ctx: StateContext<FormStateModel>) {
    ctx.patchState({
      formHistoryItem: ctx.getState()?.formItem
    })
  }
}
